import {
	type BackendMessageBody,
	createPlacement,
	type FrontendStage,
	type PlacementMessageBackendFactory,
	type PlacementMessageFrontendFactory,
} from '@post-office/placement-common-v2';
import {
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type DefaultPlacementBackend,
	type BackendSyncMessageStage,
	type AnyPayload,
	type AnyBackendMessageStage,
} from '@post-office/placement-common-v2/create-placement/types';
import {
	type CustomRenderer,
	type DefaultMessagesResponse,
} from '@post-office/placement-common-v2/types';
import type { SerializableRecord } from '@post-office/serializable-record';
import { type TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useState } from 'react';

type CustomDismissCallback = () => void;

export type PlacementRenderProps = {
	dismiss: CustomDismissCallback;
	onItemClick?: () => void;
	onItemViewed?: () => void;
};

type placementDefinition = CustomRenderer<PlacementRenderProps>;

type PlacementFrontendProps = {
	preloadedInitialData?: DefaultMessagesResponse | undefined;
	onMessageCountChange?: (numberOfMessages: number) => void;
	onItemClick?: () => void;
	onItemViewed?: () => void;
};

const placement = createPlacement<placementDefinition>({
	placementId: 'jira-navbar-assets-app-section',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
});
export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;
export const createBackend: (
	placementBackendParams?:
		| DefaultPlacementBackend<{
				transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
				hydrate?: AnyBackendMessageStage;
		  }>
		| undefined,
) => PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = placement.createBackend;

export const PlacementBackend: PlacementMessageBackendFactory<{
	transformMetadata: BackendSyncMessageStage<AnyPayload, TransformMetadataResult>;
	hydrate?: AnyBackendMessageStage;
}> = createBackend({});

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend<PlacementFrontendProps>(
	(Messages) =>
		({ onMessageCountChange, preloadedInitialData, onItemClick, onItemViewed }) => {
			const [{ messages, status }, setMessagesState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[] | undefined;
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? {
								status: 'done',
								messages: preloadedInitialData?.messages,
							}
						: { messages: undefined, status: 'done' };
				}

				return { messages: undefined, status: 'loading' };
			});

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'jira-navbar-assets-app-section',
				},
			});

			useEffect(() => {
				if (typeof messages === 'undefined') {
					return;
				}

				if (!onMessageCountChange) {
					return;
				}

				onMessageCountChange(messages.length);
			}, [messages, onMessageCountChange]);

			const handleDismiss = useCallback(
				(messageInstanceId: string): void => {
					setMessagesState(() => {
						if (!messages) {
							return { messages: undefined, status: 'done' };
						}

						const filteredMessages = messages.filter(
							(message) => messageInstanceId !== message.messageInstanceId,
						);

						return {
							messages: filteredMessages,
							status: 'done',
						};
					});
				},
				[messages],
			);

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessagesState({
						messages: data?.messages,
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			if (error) {
				return null;
			}

			if (status === 'loading') {
				return null;
			}

			return Array.isArray(messages) && messages.length > 0 ? (
				<>
					{messages?.map((props, index) => (
						<Messages.render
							key={`${props.messageInstanceId}-${index}`}
							dismiss={() => handleDismiss(props.messageInstanceId)}
							onItemClick={onItemClick}
							onItemViewed={onItemViewed}
							{...props}
						/>
					))}
				</>
			) : null;
		},
);
